import React, { FC, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Typography from '@mui/material/Typography'

import Loading from '../components/system/Loading'
import ErrorComponent from '../components/system/Error'
import DeleteConfirmWindow from '../components/system/DeleteConfirmWindow'
import { Video } from '../types'
import { listContainers, ContainerSummary, createVideo, deleteVideo as deleteVideoApi } from '../system/apitools'
import { getShortID } from '../system/id'
import useRouter from '../hooks/useRouter'
import useSnackbar from '../hooks/useSnackbar'

const thumbnailUrl = (c: ContainerSummary): string | undefined =>
  c.has_thumbnail ? `/api/v1/videos/${c.video_id}/thumbnail/image?v=${c.id}` : undefined

const ContainerThumbnail: FC<{ container: ContainerSummary }> = ({ container }) => {
  const [failed, setFailed] = useState(false)
  const url = thumbnailUrl(container)
  return (
    <Box
      sx={{
        width: 72,
        height: 72,
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: '#222',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {url && !failed ? (
        <Box
          component="img"
          src={url}
          alt={container.name || 'Video'}
          onError={() => setFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
          No preview
        </Typography>
      )}
    </Box>
  )
}

const Videos: FC = () => {
  const queryClient = useQueryClient()
  const router = useRouter()
  const snackbar = useSnackbar()
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [videoToDelete, setVideoToDelete] = useState<string | null>(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')

  const { isPending, error, data: containers } = useQuery({
    queryKey: ['containers'],
    queryFn: listContainers,
  })

  const createVideoMutation = useMutation<Video, Error>({
    mutationFn: async () => {
      return createVideo(newName, newDescription)
    },
    onSuccess: (newVideo) => {
      // ['containers'], not ['videos']: this page lists containers now, and a
      // new video always arrives with one. Invalidating the old key refreshed
      // nothing — masked only because the handler navigates away immediately.
      queryClient.invalidateQueries({ queryKey: ['containers'] })
      setAddDialogOpen(false)
      setNewName('')
      setNewDescription('')
      if (newVideo.id) {
        snackbar.success("Video created successfully")
        router.navigate('admin-video', { id: newVideo.id })
      }
    },
    onError: (error) => {
      snackbar.error("Failed to create video: " + error.message)
    }
  })

  const deleteVideoMutation = useMutation<void, Error, string>({
    mutationFn: async (videoId: string) => {
      await deleteVideoApi(videoId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['containers'] })
      setDeleteConfirmOpen(false)
      setVideoToDelete(null)
      snackbar.success("Video deleted successfully")
    },
    onError: (error) => {
      snackbar.error("Failed to delete video: " + error.message)
      setDeleteConfirmOpen(false)
      setVideoToDelete(null)
    }
  })

  if (isPending) return <Loading />
  if (error) return <ErrorComponent message={error.message} />

  // Deleting always targets the container's CURRENT version — its bytes go,
  // and data.VideoStore.DeleteVideo either stamps that version's entry
  // deleted (when that entry itself has a recorded VersionRef on chain, so
  // the on-chain list still agrees with it) or removes the entry outright
  // and renumbers the rest (when it does not — whether or not the container
  // as a whole is anchored), then deletes the whole container if nothing
  // non-deleted and no draft is left.
  const handleDeleteClick = (videoId: string) => {
    setVideoToDelete(videoId)
    setDeleteConfirmOpen(true)
  }

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false)
    setVideoToDelete(null)
  }

  const handleConfirmDelete = () => {
    if (videoToDelete) {
      deleteVideoMutation.mutate(videoToDelete)
    }
  }

  const containerBeingDeleted = containers?.find(c => c.video_id === videoToDelete)

  return (
    <div>
      <h1>Videos</h1>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setAddDialogOpen(true)}
        sx={{ mb: 2 }}
      >
        Add Video
      </Button>
      {createVideoMutation.isError && (
        <ErrorComponent message={`Failed to create video: ${createVideoMutation.error.message}`} />
      )}
      {deleteVideoMutation.isError && (
        <ErrorComponent message={`Failed to delete video: ${deleteVideoMutation.error.message}`} />
      )}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="videos table">
          <TableHead>
            <TableRow>
              <TableCell align="left">Video</TableCell>
              <TableCell align="left">Versions</TableCell>
              <TableCell align="left">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(containers || []).map((container) => (
              <TableRow
                key={container.id}
                hover
                onClick={() => router.navigate('admin-video', { id: container.video_id })}
                sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ContainerThumbnail container={container} />
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {container.name || 'Untitled'}
                        </Typography>
                        {container.private && <Chip label="Private" size="small" />}
                      </Box>
                      {container.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            maxWidth: 320,
                          }}
                        >
                          {container.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {getShortID(container.id)} · /{container.slug} ·{' '}
                        {new Date(container.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="left">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                    <Typography variant="body2">
                      {container.version_count} version{container.version_count === 1 ? '' : 's'}
                    </Typography>
                    {container.has_draft && <Chip label="Draft pending" size="small" color="info" />}
                  </Box>
                </TableCell>
                <TableCell align="left">
                  <Chip
                    label={container.state === 'ready' ? 'Ready' : 'Created'}
                    size="small"
                    color={container.state === 'ready' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={(e) => { e.stopPropagation(); handleDeleteClick(container.video_id) }}
                      disabled={deleteVideoMutation.isPending && videoToDelete === container.video_id}
                    >
                      {deleteVideoMutation.isPending && videoToDelete === container.video_id ? 'Deleting...' : 'Delete'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => { e.stopPropagation(); router.navigate('admin-video', { id: container.video_id }) }}
                    >
                      Edit
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Video Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Video</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            fullWidth
            margin="normal"
            autoFocus
          />
          <TextField
            label="Description"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createVideoMutation.mutate()}
            disabled={createVideoMutation.isPending}
          >
            {createVideoMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmWindow
        open={deleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Confirm Video Deletion"
        message={`Are you sure you want to delete the video "${containerBeingDeleted?.name || getShortID(videoToDelete || '')}"? Type "confirm" below to proceed.`}
      />
    </div>
  )
}

export default Videos
